<template>
	<a-modal
		class="g-pay-audio-player-edit-modal"
		:visible="visible"
		title="音频付费编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<PayMediaEditForm
				:form="form"
				mode="duration"
				:max-duration="form.audio?.duration || 0"
				:show-title="true"
				:show-subtitle="false"
				title-placeholder="助眠音乐"
			/>

			<div class="g-pay-audio-player-edit">
				<div
					class="g-pay-audio-player-edit__preview"
					:style="{ width: `${form.playerWidth}%` }"
				>
					<div
						class="g-pay-audio-player-edit__shell"
						:style="{ borderRadius: `${form.borderRadius}px` }"
					>
						<UploadProgressPanel
							v-if="isUploading"
							:tasks="uploadTasks"
							variant="item"
							hide-filename
						/>
						<template v-else>
						<img
							v-if="bgUrl"
							class="g-pay-audio-player-edit__bg"
							:src="bgUrl"
							alt=""
						/>
						<div
							v-if="form.darkOverlay"
							class="g-pay-audio-player-edit__overlay"
						></div>
						<div class="g-pay-audio-player-edit__content">
							<div class="g-pay-audio-player-edit__progress-wrap">
								<div
									ref="previewProgressTrack"
									class="g-pay-audio-player-edit__progress-track"
									:class="{ 'is-interactive': canPreviewPlay }"
									:style="trackStyle"
									@click="onPreviewSeek"
								>
									<div
										class="g-pay-audio-player-edit__progress-bar"
										:style="progressBarStyle"
									></div>
									<div
										class="g-pay-audio-player-edit__progress-thumb"
										:style="thumbStyle"
									></div>
								</div>
								<div class="g-pay-audio-player-edit__time" :style="timeStyle">
									<span>{{ previewCurrentTimeText }}</span>
									<span>{{ previewDurationText }}</span>
								</div>
							</div>
							<div class="g-pay-audio-player-edit__controls">
								<button
									type="button"
									class="g-pay-audio-player-edit__play"
									:style="iconStyle"
									:disabled="!canPreviewPlay"
									:aria-label="previewIsPlaying ? '暂停' : '播放'"
									@click="togglePreviewPlay"
								>
									<span v-if="previewIsPlaying" class="g-pay-audio-player-edit__pause"></span>
									<span v-else class="g-pay-audio-player-edit__triangle"></span>
								</button>
							</div>
						</div>
						<audio
							v-if="previewAudioUrl"
							ref="previewAudioEl"
							:src="previewAudioUrl"
							preload="metadata"
							@loadedmetadata="onPreviewLoadedMetadata"
							@timeupdate="onPreviewTimeUpdate"
							@ended="onPreviewEnded"
							@pause="onPreviewPause"
							@play="onPreviewPlay"
						></audio>
						</template>
					</div>
				</div>

				<div class="g-pay-audio-player-edit__panel">
					<div class="g-pay-audio-player-edit__toolbar">
						<button type="button" class="g-pay-audio-player-edit__action" @click="triggerAudioSelect">换音频</button>
						<button type="button" class="g-pay-audio-player-edit__action" @click="triggerBgSelect">换背景图</button>
					</div>
				</div>
			</div>
		</div>

		<input
			ref="bgInput"
			type="file"
			accept="image/*"
			class="g-pay-audio-player-edit__file-input"
			@change="onBgSelect"
		/>
		<input
			ref="audioInput"
			type="file"
			accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.wma"
			class="g-pay-audio-player-edit__file-input"
			@change="onAudioSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" :disabled="isUploading" @click="onConfirm">确认</a-button>
		</template>
	</a-modal>
</template>

<script>
	import PayMediaEditForm from './PayMediaEditForm.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import { normalizeHexColor } from '../utils/richText.js';
	import {
		filterValidLocalAudioList,
		revokeLocalAudioItem,
	} from '../utils/localAudio.js';
	import {
		getAudioPlayerAudioUrl,
		getAudioPlayerCoverUrl,
		replaceAudioPlayerAudio,
		replaceAudioPlayerCover,
	} from '../utils/audioPlayer.js';
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import {
		getPayAudioPlayerDefaults,
		normalizePayAudioPlayerData,
	} from '../utils/payAudioPlayer.js';
	import editModalAudioPreview from '../mixins/editModalAudioPreview.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';

	export default {
		name: 'GPayAudioPlayerEditModal',
		components: { PayMediaEditForm, UploadProgressPanel },
		mixins: [editModalAudioPreview],
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => getPayAudioPlayerDefaults(),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: getPayAudioPlayerDefaults(),
				isUploading: false,
				uploadTasks: [],
			};
		},
		computed: {
			bgUrl() {
				return getAudioPlayerCoverUrl(this.form);
			},
			previewAudioUrl() {
				return getAudioPlayerAudioUrl(this.form);
			},
			previewAudioFallbackDuration() {
				return this.form.audio?.duration || 0;
			},
			trackStyle() {
				return {
					background: this.hexToRgba(this.form.themeColor, 0.35),
				};
			},
			progressBarStyle() {
				return {
					width: `${this.previewProgressPercent}%`,
					background: this.form.themeColor,
				};
			},
			thumbStyle() {
				return {
					left: `${this.previewProgressPercent}%`,
					background: this.form.themeColor,
				};
			},
			timeStyle() {
				return { color: this.form.themeColor };
			},
			iconStyle() {
				return { color: this.form.themeColor };
			},
		},
		watch: {
			visible(value) {
				if (value) {
					this.initForm();
					this.resetPreviewPlayer();
				} else {
					this.pausePreviewAudio();
				}
			},
		},
		methods: {
			initForm() {
				this.form = normalizePayAudioPlayerData(this.data);
			},
			hexToRgba(hex, alpha) {
				const normalized = normalizeHexColor(hex).replace('#', '');
				const full = normalized.length === 3
					? normalized.split('').map(c => c + c).join('')
					: normalized;
				const num = parseInt(full, 16);
				if (Number.isNaN(num)) return `rgba(232, 76, 61, ${alpha})`;
				const r = (num >> 16) & 255;
				const g = (num >> 8) & 255;
				const b = num & 255;
				return `rgba(${r}, ${g}, ${b}, ${alpha})`;
			},
			triggerBgSelect() {
				this.$refs.bgInput?.click();
			},
			triggerAudioSelect() {
				this.$refs.audioInput?.click();
			},
			async onBgSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file)) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (this.form.cover?.local) {
						revokeLocalImageUrl(this.form.cover.previewUrl);
					}
					this.form.cover = replaceAudioPlayerCover(this.form.cover, item);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			async onAudioSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file) return;
				const [validFile] = filterValidLocalAudioList([file], 1);
				if (!validFile) return;
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([validFile]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'audio',
						files: [validFile],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					if (this.form.audio?.local) {
						revokeLocalAudioItem(this.form.audio);
					}
					this.form.audio = replaceAudioPlayerAudio(this.form.audio, item);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				this.$emit('confirm', normalizePayAudioPlayerData({ ...this.form }));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-pay-audio-player-edit__panel);
	.edit-float-toolbar(g-pay-audio-player-edit__toolbar);
	.edit-float-action(g-pay-audio-player-edit__action);

	.g-pay-audio-player-edit {
		padding: 8px 0 12px;
		border-top: 1px solid #f0f0f0;
		margin-top: 8px;
	}

	.g-pay-audio-player-edit__preview {
		margin: 0 auto 16px;
		max-width: 100%;
	}

	.g-pay-audio-player-edit__shell {
		position: relative;
		width: 100%;
		aspect-ratio: 2.35 / 1;
		overflow: hidden;
		background: #1a1a2e;
	}

	.g-pay-audio-player-edit__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}

	.g-pay-audio-player-edit__overlay {
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0.35);
		pointer-events: none;
	}

	.g-pay-audio-player-edit__content {
		position: relative;
		z-index: 1;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: 100%;
		padding: 14px 16px 12px;
		box-sizing: border-box;
	}

	.g-pay-audio-player-edit__progress-wrap {
		margin-top: auto;
		padding-top: 8px;
	}

	.g-pay-audio-player-edit__progress-track {
		position: relative;
		height: 3px;
		border-radius: 2px;

		&.is-interactive {
			cursor: pointer;
		}
	}

	.g-pay-audio-player-edit__progress-bar {
		position: absolute;
		left: 0;
		top: 0;
		height: 100%;
		border-radius: inherit;
		pointer-events: none;
	}

	.g-pay-audio-player-edit__progress-thumb {
		position: absolute;
		top: 50%;
		width: 10px;
		height: 10px;
		margin: -5px 0 0 -5px;
		border-radius: 50%;
		pointer-events: none;
	}

	.g-pay-audio-player-edit__time {
		display: flex;
		justify-content: space-between;
		margin-top: 6px;
		font-size: 11px;
		line-height: 1;
	}

	.g-pay-audio-player-edit__controls {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: 28px;
		margin-top: 4px;
	}

	.g-pay-audio-player-edit__play {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		border: none;
		background: transparent;
		color: inherit;
		cursor: pointer;
		line-height: 1;

		&:disabled {
			cursor: not-allowed;
			opacity: 0.45;
		}
	}

	.g-pay-audio-player-edit__triangle {
		display: block;
		width: 0;
		height: 0;
		margin-left: 4px;
		border-top: 10px solid transparent;
		border-bottom: 10px solid transparent;
		border-left: 16px solid currentColor;
	}

	.g-pay-audio-player-edit__pause {
		position: relative;
		display: block;
		width: 14px;
		height: 16px;

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 0;
			width: 4px;
			height: 16px;
			border-radius: 1px;
			background: currentColor;
		}

		&::before {
			left: 0;
		}

		&::after {
			right: 0;
		}
	}

	.g-pay-audio-player-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-audio-player-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();
	}
</style>
